import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, Tabs, Button, Input, Select, message } from 'antd';
import type {
    AmplifierData,
    AmplifierProjectData,
    ProgramStatusData,
    ProgramStatusMessage,
    HamlibControlRequestMessage,
    HamlibControlData,
} from './amplifier';
import ConnectionStatusTag from './ConnectionStatusTag';
import EngineeringDashboardModal from './EngineeringDashboardModal';
import { useAmplifierI18n } from '../../i18n/amplifier/useAmplifierI18n';
import { amplifierMqttService } from './AmplifierMqttService';
import { formatVersionWithUpdateHint } from './updateVersionUtils';
import './amplifierControl.css';

const ENGINEERING_ACCESS_PASSWORD = '666';

interface SystemSyncModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** 与 MQTT 主题 server/device/{deviceId}/json_command 中的 deviceId 一致 */
    deviceId: string;
    amplifierProjectData?: AmplifierProjectData;
    amplifierData?: AmplifierData | null;
    /** MQTT 下发的 update_program_status 消息，用于展示 data */
    programStatusMessage?: ProgramStatusMessage | null;
    /** 将变更字段作为 remote_control delta 发送到 MQTT */
    onProgramStatusSave?: (delta: Partial<ProgramStatusData>) => void;
    /** 触发固件/软件更新检查 */
    onDeviceUpdate?: (type: 'firmware' | 'software') => void;
    /** Hamlib set_hamlib 消息（电台设置分页展示端口、波特率） */
    hamlibControlRequestMessage?: HamlibControlRequestMessage | null;
    /** 将 Hamlib 变更字段作为 remote_control delta 发送到 MQTT */
    onHamlibControlSave?: (delta: Partial<HamlibControlData>) => void;
    /** 设备 restart 弹窗活跃时隐藏版本区「重启软件」按钮（协议 §5.7） */
    isRestartPopupOpen?: boolean;
}

const formatUnsignedDecimalDisplay = (
    value: string | number | null | undefined,
    options?: { alwaysHex?: boolean },
): string => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'number') return String(value);

    const trimmed = value.trim();
    if (!trimmed) return '—';

    const normalizedHex = trimmed.startsWith('0x') || trimmed.startsWith('0X')
        ? trimmed.slice(2)
        : trimmed;
    const isHexString = /^[0-9a-fA-F]+$/.test(normalizedHex);
    const looksExplicitlyHex = trimmed.startsWith('0x') || trimmed.startsWith('0X') || /[a-fA-F]/.test(normalizedHex);
    const shouldConvert = options?.alwaysHex ? isHexString : isHexString && looksExplicitlyHex;

    if (!shouldConvert) return trimmed;

    try {
        return BigInt(`0x${normalizedHex}`).toString(10);
    } catch {
        return trimmed;
    }
};

export const SystemSyncModal: React.FC<SystemSyncModalProps> = ({
    isOpen,
    onClose,
    deviceId,
    amplifierProjectData,
    amplifierData,
    programStatusMessage,
    onProgramStatusSave,
    onDeviceUpdate,
    hamlibControlRequestMessage,
    onHamlibControlSave,
    isRestartPopupOpen = false,
}) => {
    const { t } = useAmplifierI18n();
    const programStatusData = programStatusMessage?.data;
    const hamlibConnected = programStatusData?.hamlib_connection;
    const serialConnected = programStatusData?.serial_connection;
    const mqttConnected = programStatusData?.mqtt_connection;
    const hamlibData = hamlibControlRequestMessage?.data;
    const hamlibList = useMemo(() => hamlibData?.hamlib_list ?? [], [hamlibData?.hamlib_list]);
    const FIXED_HAMLIB_SERIAL_NONE = 'None';
    const serialPortList = useMemo(() => {
        const fromDevice = (hamlibData?.serial_port_list ?? []).filter(
            (p) => p !== FIXED_HAMLIB_SERIAL_NONE,
        );
        const current = hamlibData?.serial_port;
        if (current && current !== FIXED_HAMLIB_SERIAL_NONE && !fromDevice.includes(current)) {
            fromDevice.unshift(current);
        }
        return [FIXED_HAMLIB_SERIAL_NONE, ...fromDevice];
    }, [hamlibData?.serial_port_list, hamlibData?.serial_port]);
    const serialBaudList = useMemo(() => {
        const fromDevice = (hamlibData?.serial_boardrate_list ?? []).filter((b) => b > 0);
        const current = hamlibData?.serial_boardrate;
        if (typeof current === 'number' && current > 0 && !fromDevice.includes(current)) {
            fromDevice.unshift(current);
        }
        return fromDevice;
    }, [hamlibData?.serial_boardrate_list, hamlibData?.serial_boardrate]);
    const serialBoardrateDisplay = useMemo(() => {
        const baud = hamlibData?.serial_boardrate;
        const list = hamlibData?.serial_boardrate_list ?? [];
        if (list.length === 0 || baud == null || baud === 0) return '—';
        return String(baud);
    }, [hamlibData?.serial_boardrate, hamlibData?.serial_boardrate_list]);
    const hamlibOptions = useMemo(() => {
        const values = [...hamlibList];
        if (hamlibData?.rig && !values.includes(hamlibData.rig)) {
            values.unshift(hamlibData.rig);
        }
        return values.map((item) => ({ label: formatUnsignedDecimalDisplay(item), value: item }));
    }, [hamlibList, hamlibData?.rig]);

    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [passwordDraft, setPasswordDraft] = useState('');
    const [isEngineeringDashboardOpen, setIsEngineeringDashboardOpen] = useState(false);

    const [activeTab, setActiveTab] = useState('serial');
    const [isSerialPortSelectModalOpen, setIsSerialPortSelectModalOpen] = useState(false);
    const [isSerialBaudSelectModalOpen, setIsSerialBaudSelectModalOpen] = useState(false);
    const [isRigSelectModalOpen, setIsRigSelectModalOpen] = useState(false);
    const [rigDraft, setRigDraft] = useState<string>(hamlibData?.rig ?? '');
    /** 弹窗内最新选中值，避免 MQTT 回包触发 effect 覆盖用户选择 */
    const rigSelectionRef = useRef<string>(hamlibData?.rig ?? '');

    const [mqttIpLocal, setMqttIpLocal] = useState<string>(programStatusData?.mqtt_ip ?? '');
    const [mqttPortLocal, setMqttPortLocal] = useState<string>(programStatusData?.mqtt_port != null ? String(programStatusData.mqtt_port) : '');
    const [mqttClientIdLocal, setMqttClientIdLocal] = useState<string>(programStatusData?.mqtt_clientid ?? '');
    const [mqttUsernameLocal, setMqttUsernameLocal] = useState<string>(programStatusData?.mqtt_username ?? '');
    const [mqttPasswordLocal, setMqttPasswordLocal] = useState<string>(programStatusData?.mqtt_password ?? '');
    useEffect(() => {
        setMqttIpLocal(programStatusData?.mqtt_ip ?? '');
        setMqttPortLocal(programStatusData?.mqtt_port != null ? String(programStatusData.mqtt_port) : '');
        setMqttClientIdLocal(programStatusData?.mqtt_clientid ?? '');
        setMqttUsernameLocal(programStatusData?.mqtt_username ?? '');
        setMqttPasswordLocal(programStatusData?.mqtt_password ?? '');
    }, [programStatusData?.mqtt_ip, programStatusData?.mqtt_port, programStatusData?.mqtt_clientid, programStatusData?.mqtt_username, programStatusData?.mqtt_password]);

    const [isMqttConfigModalOpen, setIsMqttConfigModalOpen] = useState(false);
    const [mqttFormIp, setMqttFormIp] = useState('');
    const [mqttFormPort, setMqttFormPort] = useState('');
    const [mqttFormClientId, setMqttFormClientId] = useState('');
    const [mqttFormUsername, setMqttFormUsername] = useState('');
    const [mqttFormPassword, setMqttFormPassword] = useState('');
    const openMqttConfigModal = () => {
        setMqttFormIp(mqttIpLocal);
        setMqttFormPort(mqttPortLocal);
        setMqttFormClientId(mqttClientIdLocal);
        setMqttFormUsername(mqttUsernameLocal);
        setMqttFormPassword(mqttPasswordLocal);
        setIsMqttConfigModalOpen(true);
    };
    const submitMqttConfig = () => {
        const portNum = mqttFormPort.trim() === '' ? programStatusData?.mqtt_port : Number(mqttFormPort);
        const mqttPort = (typeof portNum === 'number' && !Number.isNaN(portNum)) ? portNum : (programStatusData?.mqtt_port ?? 0);
        sendProgramStatusDelta({
            mqtt_ip: mqttFormIp,
            mqtt_port: mqttPort,
            mqtt_clientid: mqttFormClientId,
            mqtt_username: mqttFormUsername,
            mqtt_password: mqttFormPassword,
        });
        setIsMqttConfigModalOpen(false);
    };

    const buildUpdateCommand = (type: 'firmware' | 'software'): void => {
        if (!deviceId?.trim() || !onDeviceUpdate) return;
        onDeviceUpdate(type);
    };

    const sendDeviceUpdate = (type: 'firmware' | 'software') => {
        buildUpdateCommand(type);
    };

    const handleRestartSoftware = () => {
        if (!deviceId?.trim()) {
            message.error(t('设备 ID 无效，无法重启软件'));
            return;
        }

        void (async () => {
            try {
                await amplifierMqttService.sendRemoteRestart(deviceId, { software: true });
                message.success(t('重启软件指令已发送'));
            } catch (error) {
                console.error('SystemSyncModal: 重启软件失败:', error);
                message.error(t('重启软件指令发送失败'));
            }
        })();
    };

    const sendProgramStatusDelta = (overrides: Partial<ProgramStatusData>) => {
        if (!onProgramStatusSave) return;
        onProgramStatusSave(overrides);
    };

    const openRigSelectModal = () => {
        const initial = hamlibData?.rig ?? hamlibList[0] ?? '';
        setRigDraft(initial);
        rigSelectionRef.current = initial;
        setIsRigSelectModalOpen(true);
    };

    const handleRigDraftChange = (value: string) => {
        setRigDraft(value);
        rigSelectionRef.current = value;
    };

    const submitRigSelection = () => {
        const selected = rigSelectionRef.current.trim();
        if (!onHamlibControlSave || !selected) return;
        onHamlibControlSave({ rig: selected });
        setIsRigSelectModalOpen(false);
    };

    const applySerialPort = (port: string) => {
        if (!onHamlibControlSave || !port.trim()) return;
        onHamlibControlSave({ serial_port: port.trim() });
    };

    const applySerialBoardrate = (baud: number) => {
        if (!onHamlibControlSave || baud <= 0) return;
        onHamlibControlSave({ serial_boardrate: baud });
    };

    const getSelectPopupContainer = (): HTMLElement =>
        typeof document !== 'undefined' ? document.body : ({} as HTMLElement);

    const openPasswordModal = () => {
        setPasswordDraft('');
        setIsPasswordModalOpen(true);
    };

    const handlePasswordConfirm = () => {
        if (passwordDraft === ENGINEERING_ACCESS_PASSWORD) {
            setIsPasswordModalOpen(false);
            setPasswordDraft('');
            setIsEngineeringDashboardOpen(true);
            return;
        }
        message.error(t('密码错误，请重试'));
        setPasswordDraft('');
    };

    const handlePasswordCancel = () => {
        setIsPasswordModalOpen(false);
        setPasswordDraft('');
    };

    return (
        <Modal
            title={(
                <button
                    type="button"
                    className="system-sync-title-trigger"
                    onClick={openPasswordModal}
                    aria-label={t('系统同步')}
                >
                    {t('系统同步')}
                </button>
            )}
            open={isOpen}
            onCancel={onClose}
            rootClassName="system-sync-modal"
            footer={[
                <Button key="cancel" onClick={onClose}>
                    {t('取消')}
                </Button>,
                <Button key="confirm" type="primary" onClick={onClose}>
                    {t('确认')}
                </Button>
            ]}
            width={800}
        >
            <div className="system-sync-body">
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={[
                        {
                            key: 'serial',
                            label: t('电台设置'),
                            children: (
                                <div className="system-sync-fields">
                                    <div className="system-sync-row">
                                        <span className="system-sync-label">{t('端口:')}</span>
                                        <div className="system-sync-row__end">
                                            <button
                                                type="button"
                                                className="system-sync-value-btn system-sync-value-btn--picker"
                                                onClick={() => setIsSerialPortSelectModalOpen(true)}
                                                disabled={serialPortList.length === 0}
                                            >
                                                {hamlibData?.serial_port || '—'}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="system-sync-row">
                                        <span className="system-sync-label">{t('波特率:')}</span>
                                        <div className="system-sync-row__end">
                                            <button
                                                type="button"
                                                className="system-sync-value-btn system-sync-value-btn--picker"
                                                onClick={() => setIsSerialBaudSelectModalOpen(true)}
                                                disabled={serialBaudList.length === 0}
                                            >
                                                {serialBoardrateDisplay}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="system-sync-row">
                                        <span className="system-sync-label">{t('Hamlib')}:</span>
                                        <div className="system-sync-row__end">
                                            <ConnectionStatusTag connected={hamlibConnected} />
                                        </div>
                                    </div>
                                    <div className="system-sync-row">
                                        <span className="system-sync-label">{t('电台:')}</span>
                                        <div className="system-sync-row__end">
                                            <span className="system-sync-value system-sync-value--mono">
                                                {formatUnsignedDecimalDisplay(hamlibData?.rig)}
                                            </span>
                                        </div>
                                    </div>
                                    <Button
                                        className="amp-sync-btn amp-sync-btn--block"
                                        onClick={openRigSelectModal}
                                        disabled={hamlibOptions.length === 0}
                                    >
                                        {t('电台选择')}
                                    </Button>
                                </div>
                            )
                        },
                        {
                            key: 'client',
                            label: t('硬件菜单'),
                            children: (
                                <div className="system-sync-fields">
                                    <div className="system-sync-row">
                                        <span className="system-sync-label">{t('硬件版本:')}</span>
                                        <div className="system-sync-action-group">
                                            <span className="system-sync-value system-sync-value--mono">
                                                {formatVersionWithUpdateHint(
                                                    formatUnsignedDecimalDisplay(programStatusData?.firmware_version),
                                                    programStatusData?.firmware_updateable ?? '',
                                                )}
                                            </span>
                                            <Button className="amp-sync-btn" onClick={() => sendDeviceUpdate('firmware')}>{t('检查更新')}</Button>
                                        </div>
                                    </div>
                                    <div className="system-sync-row">
                                        <span className="system-sync-label">{t('软件版本:')}</span>
                                        <div className="system-sync-action-group">
                                            <span className="system-sync-value system-sync-value--mono">
                                                {formatVersionWithUpdateHint(
                                                    formatUnsignedDecimalDisplay(programStatusData?.software_version),
                                                    programStatusData?.software_updateable ?? '',
                                                )}
                                            </span>
                                            <Button className="amp-sync-btn" onClick={() => sendDeviceUpdate('software')}>{t('检查更新')}</Button>
                                        </div>
                                    </div>
                                    <div className="system-sync-row">
                                        <span className="system-sync-label">{t('串口:')}</span>
                                        <span className="system-sync-value system-sync-value--mono">{formatUnsignedDecimalDisplay(programStatusData?.serial_port)}</span>
                                    </div>
                                    <div className="system-sync-row">
                                        <span className="system-sync-label">{t('连接:')}</span>
                                        <div className="system-sync-row__end">
                                            <ConnectionStatusTag connected={serialConnected} />
                                        </div>
                                    </div>
                                    <div className="system-sync-spacer-top" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                        <button
                                            type="button"
                                            className="amp-sync-btn amp-sync-btn--block"
                                            onClick={() => sendProgramStatusDelta({ mcu_reconnection: true })}
                                        >
                                            {t('重新连接')}
                                        </button>
                                        {!isRestartPopupOpen && (
                                            <button
                                                type="button"
                                                className="amp-sync-btn amp-sync-btn--block"
                                                onClick={handleRestartSoftware}
                                            >
                                                {t('重启软件')}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )
                        },
                        {
                            key: 'server',
                            label: t('服务器设置'),
                            children: (
                                <div className="system-sync-fields">
                                    <div className="system-sync-row">
                                        <span className="system-sync-label">IP:</span>
                                        <span className="system-sync-value system-sync-value--mono">{formatUnsignedDecimalDisplay(mqttIpLocal)}</span>
                                    </div>
                                    <div className="system-sync-row">
                                        <span className="system-sync-label">PORT:</span>
                                        <span className="system-sync-value system-sync-value--mono">{formatUnsignedDecimalDisplay(mqttPortLocal)}</span>
                                    </div>
                                    <div className="system-sync-row">
                                        <span className="system-sync-label">ClientID:</span>
                                        <span className="system-sync-value system-sync-value--mono">{formatUnsignedDecimalDisplay(mqttClientIdLocal, { alwaysHex: true })}</span>
                                    </div>
                                    <div className="system-sync-row">
                                        <span className="system-sync-label">UserName:</span>
                                        <span className="system-sync-value system-sync-value--mono">{formatUnsignedDecimalDisplay(mqttUsernameLocal)}</span>
                                    </div>
                                    <div className="system-sync-row">
                                        <span className="system-sync-label">PassWd:</span>
                                        <span className="system-sync-value system-sync-value--mono">{mqttPasswordLocal ? '••••••' : '—'}</span>
                                    </div>
                                    <div className="system-sync-row">
                                        <span className="system-sync-label">{t('连接:')}</span>
                                        <div className="system-sync-row__end">
                                            <ConnectionStatusTag connected={mqttConnected} />
                                        </div>
                                    </div>
                                    <Button
                                        className="amp-sync-btn amp-sync-btn--block system-sync-spacer-sm"
                                        onClick={openMqttConfigModal}
                                    >
                                        {t('配置')}
                                    </Button>
                                </div>
                            )
                        }
                    ]}
                />
            </div>

            <Modal
                title={t('MQTT 配置')}
                open={isMqttConfigModalOpen}
                onCancel={() => setIsMqttConfigModalOpen(false)}
                rootClassName="system-sync-submodal"
                footer={[
                    <Button key="cancel" onClick={() => setIsMqttConfigModalOpen(false)}>{t('取消')}</Button>,
                    <Button key="confirm" type="primary" onClick={submitMqttConfig}>{t('确认')}</Button>,
                ]}
                width={480}
                destroyOnClose
            >
                <div className="system-sync-form">
                    <div className="system-sync-form-row">
                        <span className="system-sync-label">Host / IP :</span>
                        <Input
                            value={mqttFormIp}
                            onChange={(e) => setMqttFormIp(e.target.value)}
                            className="system-sync-input"
                        />
                    </div>
                    <div className="system-sync-form-row">
                        <span className="system-sync-label">Port :</span>
                        <Input
                            value={mqttFormPort}
                            onChange={(e) => setMqttFormPort(e.target.value)}
                            className="system-sync-input"
                        />
                    </div>
                    <div className="system-sync-form-row">
                        <span className="system-sync-label">ClientId :</span>
                        <Input
                            value={mqttFormClientId}
                            onChange={(e) => setMqttFormClientId(e.target.value)}
                            className="system-sync-input"
                        />
                    </div>
                    <div className="system-sync-form-row">
                        <span className="system-sync-label">Username :</span>
                        <Input
                            value={mqttFormUsername}
                            onChange={(e) => setMqttFormUsername(e.target.value)}
                            className="system-sync-input"
                        />
                    </div>
                    <div className="system-sync-form-row">
                        <span className="system-sync-label">Password :</span>
                        <Input
                            value={mqttFormPassword}
                            onChange={(e) => setMqttFormPassword(e.target.value)}
                            className="system-sync-input"
                        />
                    </div>
                </div>
            </Modal>

            <Modal
                title={t('串口选择')}
                open={isSerialPortSelectModalOpen}
                onCancel={() => setIsSerialPortSelectModalOpen(false)}
                rootClassName="system-sync-submodal"
                footer={[
                    <Button key="close" onClick={() => setIsSerialPortSelectModalOpen(false)}>{t('关闭')}</Button>,
                ]}
                width={480}
                destroyOnClose
                classNames={{ body: 'ant-modal-body--scroll' }}
            >
                <div className="system-sync-list">
                    <span className="system-sync-hint">
                        {t('共 {count} 个可用串口，点击切换 Hamlib 串口', { count: serialPortList.length })}
                    </span>
                    {serialPortList.map((port) => {
                        const isCurrent = hamlibData?.serial_port === port;
                        return (
                            <Button
                                key={port}
                                block
                                className={`amp-sync-btn amp-sync-btn--list${isCurrent ? ' amp-sync-btn--selected' : ''}`}
                                onClick={() => applySerialPort(port)}
                            >
                                {port}
                            </Button>
                        );
                    })}
                </div>
            </Modal>

            <Modal
                title={t('波特率选择')}
                open={isSerialBaudSelectModalOpen}
                onCancel={() => setIsSerialBaudSelectModalOpen(false)}
                rootClassName="system-sync-submodal"
                footer={[
                    <Button key="close" onClick={() => setIsSerialBaudSelectModalOpen(false)}>{t('关闭')}</Button>,
                ]}
                width={480}
                destroyOnClose
                classNames={{ body: 'ant-modal-body--scroll' }}
            >
                <div className="system-sync-list">
                    <span className="system-sync-hint">
                        {t('共 {count} 个可选波特率，点击切换 Hamlib 波特率', { count: serialBaudList.length })}
                    </span>
                    {serialBaudList.map((baud) => {
                        const isCurrent = hamlibData?.serial_boardrate === baud;
                        return (
                            <Button
                                key={baud}
                                block
                                className={`amp-sync-btn amp-sync-btn--list${isCurrent ? ' amp-sync-btn--selected' : ''}`}
                                onClick={() => applySerialBoardrate(baud)}
                            >
                                {baud}
                            </Button>
                        );
                    })}
                </div>
            </Modal>

            <Modal
                title={t('电台选择')}
                open={isRigSelectModalOpen}
                onCancel={() => setIsRigSelectModalOpen(false)}
                rootClassName="system-sync-submodal"
                footer={[
                    <Button key="cancel" onClick={() => setIsRigSelectModalOpen(false)}>{t('取消')}</Button>,
                    <Button key="confirm" type="primary" onClick={submitRigSelection} disabled={!rigDraft}>
                        {t('确认')}
                    </Button>,
                ]}
                width={520}
                destroyOnClose
            >
                <div className="system-sync-list" style={{ gap: 12 }}>
                    <span className="system-sync-hint">
                        {t('共 {count} 个 Hamlib 机型', { count: hamlibOptions.length })}
                    </span>
                    <Select
                        value={rigDraft || undefined}
                        onChange={handleRigDraftChange}
                        onSelect={handleRigDraftChange}
                        options={hamlibOptions}
                        placeholder={t('请选择电台')}
                        style={{ width: '100%' }}
                        getPopupContainer={getSelectPopupContainer}
                        showSearch
                        optionFilterProp="label"
                        listHeight={320}
                    />
                </div>
            </Modal>

            <Modal
                title={t('密码验证')}
                open={isPasswordModalOpen}
                onOk={handlePasswordConfirm}
                onCancel={handlePasswordCancel}
                okText={t('确认')}
                cancelText={t('取消')}
                rootClassName="system-sync-submodal"
                destroyOnClose
            >
                <Input.Password
                    placeholder={t('请输入密码')}
                    value={passwordDraft}
                    onChange={(e) => setPasswordDraft(e.target.value)}
                    onPressEnter={handlePasswordConfirm}
                    maxLength={10}
                    autoFocus
                />
            </Modal>

            <EngineeringDashboardModal
                open={isEngineeringDashboardOpen}
                onClose={() => setIsEngineeringDashboardOpen(false)}
                deviceId={deviceId}
                amplifierProjectData={amplifierProjectData}
                amplifierData={amplifierData}
            />
        </Modal>
    );
};

export default SystemSyncModal;